import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { listGuests } from "../cbt/api/guests";
import {
  setBookingFormFieldValue,
  setBookingFormValue,
} from "../data/actions/bookingForm";
import { setCbtValue } from "../data/actions/cbt";
import { setFormValue } from "../data/actions/forms";
import { getCbtToken } from "../data/customerTokens";
import { AppState } from "../data/reducers";
import { getCountryCodeFromTelephoneNumber } from "../lib/result";

export function usePassengerCbtGuestSearch({
  setShowCbtGuestSearchForm,
  selectedGrade,
}) {
  const [suggestedGuests, setSuggestedGuests] = useState(null);

  const {
    cbtApiUrl,
    customerUsername,
    guestsCache,
    guestsUsedInDynamicForm,
    loyaltyProgramsCodebook,
    bookingFormFields,
    cbtGuestsInDynamicFormShowingSearchForm,
    cbtGuestsUsedInDynamicForm,
    bookingFormSections,
  } = useSelector((state: AppState) => ({
    cbtApiUrl:
      state.storage.frontendSettings?.dealerCorporateSettings?.cbtApiUrl,
    customerUsername: state.user.email,
    guestsCache: state.cbt.guestsCache,
    guestsUsedInDynamicForm: state.cbt.guestsUsedInDynamicForm,
    loyaltyProgramsCodebook: state.forms.codebook?.LoyaltyPrograms?.Program,
    bookingFormFields: state.bookingForm.bookingFormFields,
    cbtGuestsInDynamicFormShowingSearchForm:
      state.bookingForm.cbtGuestsInDynamicFormShowingSearchForm,
    cbtGuestsUsedInDynamicForm: state.bookingForm.cbtGuestsUsedInDynamicForm,
    bookingFormSections: state.bookingForm.bookingFormSections,
  }));

  const dispatch = useDispatch();

  const searchGuests = async (searchText) => {
    if (!searchText || searchText.length < 3) {
      return setSuggestedGuests(null);
    }

    const cbtToken = await getCbtToken();

    const guestsResp = await listGuests({
      cbtToken,
      query: searchText.length >= 3 ? searchText : "",
      customerUsername,
      cbtApiUrl,
    });

    if (guestsResp.success && guestsResp.data) {
      return setSuggestedGuests(guestsResp.data.data);
    }
  };

  const usedGuestsIds = guestsUsedInDynamicForm
    ? Object.keys(guestsUsedInDynamicForm).map(
        (key) => guestsUsedInDynamicForm[key]
      )
    : [];

  const isHotel = !!bookingFormFields["0-traveler_title"];

  const guestsOptionsOld = (
    suggestedGuests ||
    (guestsCache?.length > 0 && guestsCache) ||
    []
  )
    .map((suggestedGuest, index) => ({
      value: index,
      ...suggestedGuest,
    }))
    .filter((suggestedGuest) => {
      if (selectedGrade && suggestedGuest.GradeId !== selectedGrade) {
        return false;
      }

      return (
        usedGuestsIds.length === 0 || !usedGuestsIds.includes(suggestedGuest.Id)
      );
    });

  const guestsOptions = (idx: null | number = null) =>
    (suggestedGuests || (guestsCache?.length > 0 && guestsCache) || [])
      .map((suggestedGuest, index) => ({
        value: index,
        ...suggestedGuest,
      }))
      .filter((suggestedGuest) => {
        if (selectedGrade && suggestedGuest.GradeId !== selectedGrade) {
          return false;
        }

        if (idx !== null) {
          const allGuests = bookingFormSections.filter(
            (section) =>
              section.code?.includes("passenger") &&
              section.title?.includes("PASSENGER")
          );

          const guest = allGuests[idx];
          const guestType = guest?.title?.split("-").pop();

          return guestType === suggestedGuest?.PassengerTypeCode;
        }

        return (
          usedGuestsIds.length === 0 ||
          !usedGuestsIds.includes(suggestedGuest.Id)
        );
      });

  const guestOptionsResolved = (idx: null | number = null) => {
    if (isHotel) {
      return guestsOptionsOld;
    }
    return guestsOptions(idx);
  };

  const onSelectFoundGuest = (foundGuest, idx) => {
    const elementMap = {
      PhoneNumber: [
        `${idx}-passenger_phone_country`,
        `${idx}-passenger_phone_number`,
      ],
      NamePrefix: `${idx}-passenger_title`,
      FirstNameLatin: `${idx}-passenger_firstname`,
      LastNameLatin: `${idx}-passenger_lastname`,
      BirthDate: [`${idx}-passenger_birth_date`, `${idx}-DateOfBirth`],
      Number: [`${idx}-passenger_passport_number`, `${idx}-PassportNumber`],
      Validity: [
        `${idx}-passenger_passport_validity`,
        `${idx}-PassportExpiryDate`,
      ],
      Citizenship: `${idx}-passenger_citizenship`,
      CountryOfIssue: [
        `${idx}-passenger_passport_country_of_issue`,
        `${idx}-PassportCountryOfIssue`,
      ],
      Email: `${idx}-passenger_email`,
      loyaltyProgram: "loyaltyProgram",
      GradeId: `${idx}-passenger_grade`,
    };

    const savedGuestDataToDynamicForm = saveGuestDataToDynamicForm({
      foundGuest,
      elementMap,
      loyaltyProgramsCodebook,
      dispatch,
      bookingFormFields,
    });

    const newGuestsUsedInDynamicForm = {
      ...guestsUsedInDynamicForm,
      ...savedGuestDataToDynamicForm,
    };

    dispatch(
      setCbtValue("guestsUsedInDynamicForm", newGuestsUsedInDynamicForm)
    );
    dispatch(
      setBookingFormValue("cbtGuestsUsedInDynamicForm", {
        ...cbtGuestsUsedInDynamicForm,
        ...savedGuestDataToDynamicForm,
      })
    );
    const newCbtGuestsInDynamicFormShowingSearchForm = cbtGuestsInDynamicFormShowingSearchForm.filter(
      (index) => Number(index) !== Number(idx)
    );

    dispatch(
      setBookingFormValue(
        "cbtGuestsInDynamicFormShowingSearchForm",
        newCbtGuestsInDynamicFormShowingSearchForm
      )
    );

    // add selected guest to reducer, so that we can use it as base when PUTting update (whole object must be sent)
    if (
      !guestsCache ||
      guestsCache.length === 0 ||
      !guestsCache.find((guest) => guest.Id === foundGuest.Id)
    ) {
      dispatch(
        setCbtValue(
          "guestsCache",
          (guestsCache || []).concat({ ...foundGuest, value: undefined })
        )
      );
    }

    setShowCbtGuestSearchForm(false);
  };

  return {
    searchGuests,
    guestsOptions: guestOptionsResolved,
    onSelectFoundGuest,
  };
}

function saveGuestDataToDynamicForm({
  foundGuest,
  elementMap,
  loyaltyProgramsCodebook,
  dispatch,
  bookingFormFields,
}) {
  const values = Object.values(elementMap);
  const indexGuest = Array.isArray(values[0])
    ? (values[0][0] as string).split("-")[0]
    : (values[0] as string).split("-")[0];
  const isHotel = !!bookingFormFields["0-traveler_title"];

  Object.keys(elementMap).forEach((key) => {
    const arrayOfKeys = Array.isArray(elementMap[key])
      ? elementMap[key]
      : [elementMap[key]];

    arrayOfKeys.forEach((foundKeyBase) => {
      const foundKey = isHotel
        ? (foundKeyBase as string).split("passenger").join("traveler")
        : foundKeyBase;

      const valueFound = foundGuest[key] ?? foundGuest.Passports?.[0]?.[key];

      if (foundKey?.includes("phone")) {
        const { phoneNumber } = getCountryCodeFromTelephoneNumber(valueFound);
        saveValue({
          dispatch,
          bookingFormFields,
          key: arrayOfKeys[0],
          value: valueFound,
        });

        dispatch(setFormValue(arrayOfKeys[0], { value: valueFound }));

        dispatch(setFormValue(arrayOfKeys[1], { value: phoneNumber }));
      } else if (valueFound && foundKey) {
        let adjustedValue = valueFound;
        if (key === "NamePrefix") {
          const fieldToUpdate = bookingFormFields[foundKey];

          const selectedOption = fieldToUpdate.options.find(
            (option) => option.value.toLowerCase() === valueFound.toLowerCase()
          );
          if (selectedOption) {
            adjustedValue = selectedOption.value;
          } else {
            adjustedValue = fieldToUpdate.options[0].value;
          }
        }

        if (foundKey?.includes("birth")) {
          const birthDateKey = arrayOfKeys.find((bkey) =>
            bkey.includes("passenger_birth_date")
          );

          const birthDateField = bookingFormFields?.[birthDateKey];
          const dateRange = birthDateField?.dateRange;

          if (!dateRange) {
            return;
          }

          const valueDate = new Date(valueFound);
          const rangeStart = new Date(dateRange.from);
          const rangeEnd = new Date(dateRange.to);

          const isWithinRange =
            valueDate >= rangeStart && valueDate <= rangeEnd;

          if (!isWithinRange) {
            return;
          }
        }

        saveValue({
          dispatch,
          bookingFormFields,
          key: foundKey,
          value: adjustedValue,
        });
        dispatch(setFormValue(foundKey, { value: valueFound }));
      } else if (valueFound === undefined && key === "loyaltyProgram") {
        const foundLoyaltyProgram = foundGuest.FrequentFlyers.find(
          (customerProgram) =>
            loyaltyProgramsCodebook?.find(
              (programList) => customerProgram.AirCompany === programList.Code
            )
        );

        if (foundLoyaltyProgram) {
          saveValue({
            dispatch,
            bookingFormFields,
            key: `${indexGuest}-passenger_frequent_flyer`,
            value: foundLoyaltyProgram?.AirCompany,
          });
          dispatch(
            setFormValue(`${indexGuest}-passenger_frequent_flyer`, {
              value: foundLoyaltyProgram?.AirCompany,
            })
          );

          saveValue({
            dispatch,
            bookingFormFields,
            key: `${indexGuest}-passenger_frequent_flyer_number`,
            value: foundLoyaltyProgram?.Number,
          });
          dispatch(
            setFormValue(`${indexGuest}-passenger_frequent_flyer_number`, {
              value: foundLoyaltyProgram?.Number,
            })
          );
        }
      }
    });
  });

  return { [indexGuest]: foundGuest.Id };
}

function saveValue({ dispatch, bookingFormFields, key, value }) {
  const isHotel = !!bookingFormFields["0-traveler_title"];
  const keyTransformedToHotelOrFlightVariant = isHotel
    ? key.split("passenger").join("traveler")
    : key;

  return dispatch(
    setBookingFormFieldValue(keyTransformedToHotelOrFlightVariant, {
      ...bookingFormFields[keyTransformedToHotelOrFlightVariant],
      value,
    })
  );
}
